import { computed, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'

/**
 * 运营配置模块识别 Hook
 *
 * 场景：系统页面（views/mp/*、views/cms/site、DomainManagement、ModuleDict 等）被多个模块复用，
 * 通过当前路由 meta 判断所属业务模块，从而选择模块级接口（scenic/food），而不是系统接口。
 *
 * 模块来源优先级：
 * 1. route.query.module —— URL 参数（如 /dict/type/data/xxx?module=scenic）
 * 2. route.meta?.query?.module —— 动态菜单 component 携带参数，如 system/dict/ModuleDict?module=scenic
 * 3. route.meta?.module —— 静态路由显式声明，如 catering-app.ts meta.module='food'
 * 4. route.meta?.moduleCode —— 动态菜单的 module_code 字段（scenic/food）
 * 5. 路径兜底：/repast/ → food；/scenic/ → scenic
 *
 * 返回：'scenic' | 'food' | ''（空字符串 = 系统模块，走原系统接口）
 */
const resolveKey = (route: ReturnType<typeof useRoute>): string => {
  // 1. URL query 参数（如 /dict/type/data/xxx?module=scenic）
  const fromUrlQuery = route.query?.module
  // 2. 动态菜单 component 携带参数（如 system/dict/ModuleDict?module=scenic → meta.query.module）
  const fromMetaQuery = route.meta?.query?.module
  // 3. 静态路由显式声明（如 catering-app.ts meta.module='food'）
  const fromMeta = route.meta?.module
  // 4. 动态菜单的 module_code 字段
  const fromCode = route.meta?.moduleCode
  const raw = [fromUrlQuery, fromMetaQuery, fromMeta, fromCode]
    .map((v) => (Array.isArray(v) ? v[0] : v))
    .find((v) => typeof v === 'string' && v.length > 0)
  if (typeof raw === 'string' && raw.length > 0) {
    const key = raw.toLowerCase()
    if (key === 'scenic') return 'scenic'
    if (key === 'food' || key === 'repast' || key === 'catering') return 'food'
  }
  // 路径兜底
  const path = route.path.toLowerCase()
  if (path.startsWith('/repast/')) return 'food'
  if (path.startsWith('/scenic/')) return 'scenic'
  return ''
}

export const useModuleKey = (): ComputedRef<string> => {
  const route = useRoute()
  return computed(() => resolveKey(route))
}

/**
 * 同步获取模块标识（用于 setup 中一次性选择 API 对象，避免 computed 包装）
 * 注意：页面级路由切换时组件可能复用（keep-alive），如需响应式请使用 useModuleKey()
 */
export const getModuleKey = (): string => {
  const route = useRoute()
  return resolveKey(route)
}
